import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Loader } from '../components/ui/Loader';
import { useGetUserProfileQuery } from '../store/api/userApi';
import { useGetBalanceQuery } from '../store/api/balanceApi';
import { useGetMyTransactionsQuery } from '../store/api/balanceApi';
import { useGetProxiesQuery } from '../store/api/proxiesApi';
import { useGetPermanentNumbersQuery } from '../store/api/permanentNumbersApi';
import { useGetTemporaryNumbersQuery } from '../store/api/temporaryNumbersApi';
import { formatDate } from '../utils/dateFormatter';
import { useLanguage } from '../hooks/useLanguage';

function DashboardPage() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  // Fetch user profile
  const { data: userData, isLoading: loadingUser } = useGetUserProfileQuery();
  const user = userData?.data.user;

  // Fetch balance
  const { data: balanceData, isLoading: loadingBalance } = useGetBalanceQuery();

  // Fetch recent transactions
  const { data: transactionsData, isLoading: loadingTransactions } = useGetMyTransactionsQuery({
    limit: 5,
    page: 1,
  });

  // Fetch services statistics
  const { data: proxiesData } = useGetProxiesQuery({ status: 'active', limit: 1 });
  const { data: permanentNumbersData } = useGetPermanentNumbersQuery({ status: 'active', limit: 1 });
  const { data: temporaryNumbersData } = useGetTemporaryNumbersQuery({ status: 'active', limit: 1 });

  const isLoading = loadingUser || loadingBalance || loadingTransactions;

  // Calculate total balance
  const totalBalance = useMemo(() => {
    if (!balanceData?.data) return 0;
    return (
      balanceData.data.balance +
      balanceData.data.referralBalance +
      balanceData.data.cashbackBalance
    );
  }, [balanceData]);

  // Get statistics
  const activeProxies = proxiesData?.data.pagination.total || 0;
  const activePermanentNumbers = permanentNumbersData?.data.pagination.total || 0;
  const activeTemporaryNumbers = temporaryNumbersData?.data.pagination.total || 0;
  const recentTransactions = transactionsData?.data.transactions || [];

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="mb-8 text-center animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
              {t('dashboard.title')}
            </h1>
            <p className="text-neutral-medium text-lg font-body">
              {t('dashboard.subtitle', { name: user?.firstName || t('dashboard.user') })}
            </p>
          </div>

          {/* Balance Cards – على الموبايل: كل اثنين جنب بعض (2x2) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 animate-scale-in">
            {/* Main Balance */}
            <div className="card-glass rounded-2xl shadow-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-neutral-medium font-body text-sm">
                  {t('dashboard.balance.main')}
                </h3>
                <svg
                  className="w-6 h-6 text-brand-accent"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p className="text-3xl font-heading font-bold text-brand-accent">
                ${balanceData?.data.balance.toFixed(2) || '0.00'}
              </p>
            </div>

            {/* Referral Balance */}
            <div className="card-glass rounded-2xl shadow-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-neutral-medium font-body text-sm">
                  {t('dashboard.balance.referral')}
                </h3>
                <svg
                  className="w-6 h-6 text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <p className="text-3xl font-heading font-bold text-green-400">
                ${balanceData?.data.referralBalance.toFixed(2) || '0.00'}
              </p>
            </div>

            {/* Cashback Balance */}
            <div className="card-glass rounded-2xl shadow-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-neutral-medium font-body text-sm">
                  {t('dashboard.balance.cashback')}
                </h3>
                <svg
                  className="w-6 h-6 text-yellow-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p className="text-3xl font-heading font-bold text-yellow-400">
                ${balanceData?.data.cashbackBalance.toFixed(2) || '0.00'}
              </p>
            </div>

            {/* Total Balance */}
            <div className="bg-brand-accent/20 backdrop-blur-lg rounded-2xl shadow-2xl border border-brand-accent/50 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-neutral-light font-body text-sm font-semibold">
                  {t('dashboard.balance.total')}
                </h3>
                <svg
                  className="w-6 h-6 text-brand-accent"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <p className="text-3xl font-heading font-bold text-brand-accent">
                ${totalBalance.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Services Statistics & Account Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Services Statistics */}
            <div className="card-glass rounded-2xl shadow-2xl p-6 animate-scale-in">
              <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
                {t('dashboard.services.title')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Active Proxies */}
                <Link
                  to="/proxies/list"
                  className="bg-neutral-dark rounded-lg p-4 hover:bg-neutral-dark/80 transition-all border border-neutral-dark hover:border-brand-accent/50"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-neutral-medium font-body text-sm">
                      {t('dashboard.services.proxies')}
                    </span>
                    <svg
                      className="w-5 h-5 text-brand-accent"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
                      />
                    </svg>
                  </div>
                  <p className="text-2xl font-heading font-bold text-brand-accent">
                    {activeProxies}
                  </p>
                </Link>

                {/* Active Permanent Numbers */}
                <Link
                  to="/permanent-numbers/list"
                  className="bg-neutral-dark rounded-lg p-4 hover:bg-neutral-dark/80 transition-all border border-neutral-dark hover:border-brand-accent/50"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-neutral-medium font-body text-sm">
                      {t('dashboard.services.permanentNumbers')}
                    </span>
                    <svg
                      className="w-5 h-5 text-brand-accent"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-2xl font-heading font-bold text-brand-accent">
                    {activePermanentNumbers}
                  </p>
                </Link>

                {/* Active Temporary Numbers */}
                <Link
                  to="/temporary-numbers/list"
                  className="bg-neutral-dark rounded-lg p-4 hover:bg-neutral-dark/80 transition-all border border-neutral-dark hover:border-brand-accent/50"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-neutral-medium font-body text-sm">
                      {t('dashboard.services.temporaryNumbers')}
                    </span>
                    <svg
                      className="w-5 h-5 text-brand-accent"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                  <p className="text-2xl font-heading font-bold text-brand-accent">
                    {activeTemporaryNumbers}
                  </p>
                </Link>
              </div>
            </div>

            {/* Account Statistics */}
            <div className="card-glass rounded-2xl shadow-2xl p-6 animate-scale-in">
              <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
                {t('dashboard.account.title')}
              </h2>
              <div className="space-y-4">
                {/* Referrals */}
                <div className="flex items-center justify-between p-4 bg-neutral-dark rounded-lg">
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-5 h-5 text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                    <span className="text-neutral-medium font-body">
                      {t('dashboard.account.referrals')}
                    </span>
                  </div>
                  <span className="text-xl font-heading font-bold text-brand-accent">
                    {user?.referralCount || 0}
                  </span>
                </div>

                {/* API Keys */}
                <Link
                  to="/profile"
                  className="flex items-center justify-between p-4 bg-neutral-dark rounded-lg hover:bg-neutral-dark/80 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-5 h-5 text-brand-accent"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                      />
                    </svg>
                    <span className="text-neutral-medium font-body">
                      {t('dashboard.account.apiKeys')}
                    </span>
                  </div>
                  <span className="text-xl font-heading font-bold text-brand-accent">
                    {user?.apiKeysCount || 0}
                  </span>
                </Link>

                {/* Account Created */}
                {user?.createdAt && (
                  <div className="flex items-center justify-between p-4 bg-neutral-dark rounded-lg">
                    <div className="flex items-center gap-3">
                      <svg
                        className="w-5 h-5 text-neutral-medium"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="text-neutral-medium font-body">
                        {t('dashboard.account.createdAt')}
                      </span>
                    </div>
                    <span className="text-sm font-body text-neutral-light">
                      {formatDate(user.createdAt, 'PP h:mm a', currentLanguage)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Transactions & Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Transactions */}
            <div className="card-glass rounded-2xl shadow-2xl p-6 animate-scale-in">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-heading font-semibold text-neutral-light">
                  {t('dashboard.transactions.title')}
                </h2>
                <Link
                  to="/transactions"
                  className="text-brand-accent hover:text-brand-accent/80 font-body text-sm transition-colors"
                >
                  {t('dashboard.transactions.viewAll')} →
                </Link>
              </div>
              {recentTransactions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-neutral-medium font-body">
                    {t('dashboard.transactions.empty')}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentTransactions.map((transaction) => {
                    // Map transaction type from snake_case to camelCase for translation keys
                    const getTransactionTypeLabel = (type: string): string => {
                      const typeMap: Record<string, string> = {
                        recharge: 'recharge',
                        purchase: 'purchase',
                        referral_bonus: 'referralBonus',
                        refund: 'refund',
                        admin_add: 'adminAdd',
                        admin_deduct: 'adminDeduct',
                        cashback_claim: 'cashbackClaim',
                        cashback_earn: 'cashbackEarn',
                        admin_add_referral: 'adminAddReferral',
                        admin_deduct_referral: 'adminDeductReferral',
                        admin_add_cashback: 'adminAddCashback',
                        admin_deduct_cashback: 'adminDeductCashback',
                      };
                      return typeMap[type] ?? type;
                    };

                    return (
                    <div
                      key={transaction._id}
                      className="flex items-center justify-between p-4 bg-neutral-dark rounded-lg"
                    >
                      <div className="flex-1">
                        <p className="text-neutral-light font-body font-semibold">
                          {t(`transactions.types.${getTransactionTypeLabel(transaction.type)}`)}
                        </p>
                        <p className="text-sm text-neutral-medium font-body">
                          {formatDate(transaction.createdAt, 'PP h:mm a', currentLanguage)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p
                          className={`text-lg font-heading font-bold ${
                            transaction.type === 'recharge' ||
                            transaction.type === 'referral_bonus' ||
                            transaction.type === 'cashback_claim' ||
                            transaction.type === 'cashback_earn' ||
                            transaction.type === 'refund' ||
                            transaction.type === 'admin_add' ||
                            transaction.type === 'admin_add_referral' ||
                            transaction.type === 'admin_add_cashback'
                              ? 'text-green-400'
                              : 'text-red-400'
                          }`}
                        >
                          {transaction.type === 'recharge' ||
                          transaction.type === 'referral_bonus' ||
                          transaction.type === 'cashback_claim' ||
                          transaction.type === 'cashback_earn' ||
                          transaction.type === 'refund' ||
                          transaction.type === 'admin_add' ||
                          transaction.type === 'admin_add_referral' ||
                          transaction.type === 'admin_add_cashback'
                            ? '+'
                            : '-'}
                          ${Math.abs(transaction.amount).toFixed(2)}
                        </p>
                        <p
                          className={`text-xs font-body ${
                            transaction.status === 'completed'
                              ? 'text-green-400'
                              : transaction.status === 'failed'
                                ? 'text-red-400'
                                : 'text-yellow-400'
                          }`}
                        >
                          {t(`transactions.status.${transaction.status}`)}
                        </p>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="card-glass rounded-2xl shadow-2xl p-6 animate-scale-in">
              <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
                {t('dashboard.quickActions.title')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link
                  to="/proxies/purchase"
                  className="p-4 bg-neutral-dark rounded-lg border border-neutral-dark hover:border-brand-accent/50 transition-all text-center"
                >
                  <svg
                    className="w-8 h-8 text-brand-accent mx-auto mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
                    />
                  </svg>
                  <p className="text-neutral-light font-body font-semibold">
                    {t('dashboard.quickActions.purchaseProxy')}
                  </p>
                </Link>

                <Link
                  to="/permanent-numbers/purchase"
                  className="p-4 bg-neutral-dark rounded-lg border border-neutral-dark hover:border-brand-accent/50 transition-all text-center flex flex-col items-center justify-center gap-1"
                >
                  <svg
                    className="w-8 h-8 text-brand-accent mx-auto mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-neutral-light font-body font-semibold">
                    {t('dashboard.quickActions.purchasePermanentNumber')}
                  </p>
                </Link>

                <Link
                  to="/temporary-numbers"
                  className="p-4 bg-neutral-dark rounded-lg border border-neutral-dark hover:border-brand-accent/50 transition-all text-center"
                >
                  <svg
                    className="w-8 h-8 text-brand-accent mx-auto mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <p className="text-neutral-light font-body font-semibold">
                    {t('dashboard.quickActions.purchaseTemporaryNumber')}
                  </p>
                </Link>

                <Link
                  to="/recharge"
                  className="p-4 bg-neutral-dark rounded-lg border border-neutral-dark hover:border-brand-accent/50 transition-all text-center"
                >
                  <svg
                    className="w-8 h-8 text-brand-accent mx-auto mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  <p className="text-neutral-light font-body font-semibold">
                    {t('dashboard.quickActions.recharge')}
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
